import { type ReactNode, useLayoutEffect, useRef } from "react";
import type { SettingsDomain, SettingsResponse } from "../../shared/types";
import { useAuth } from "../auth/AuthContext";
import { IconButton } from "../components/ui/Button";
import { cx } from "../components/ui/cx";
import { Icon } from "../components/ui/Icon";
import { useToast } from "../components/ui/Toast";
import { type Colours, LOOKS, type LookId } from "./looks";
import { initialsOf, type PreviewRoom, type PreviewWho } from "./Previews";
import { MX_PRIORITY, recordName } from "./rules";

/** Copy a value and say so. */
export function useCopy(): (value: string, said?: string) => void {
  const toast = useToast();
  return (value, said = "Copied") => {
    void navigator.clipboard
      ?.writeText(value)
      .then(() => toast({ text: said, icon: "copy" }))
      .catch(() => toast({ text: "Copy didn’t work. Select it and copy it yourself.", icon: "info" }));
  };
}

/** A value to copy (`.copyv`), with its name in front when it has one. */
export function CopyValue({ value, label, shown, title = "Copy" }: { value: string; label?: string; shown?: ReactNode; title?: string }) {
  const copy = useCopy();
  return (
    <div className="copyv">
      {label ? <em>{label}</em> : null}
      <span>{shown ?? value}</span>
      <IconButton icon="copy" label={title} onClick={() => copy(value)} />
    </div>
  );
}

/** The address every link uses: the business's own once it is connected, its free one until then. */
export function shownHost(d: SettingsDomain): string {
  return d.custom_domain_status === "verified" && d.custom_domain ? d.custom_domain : d.subdomain_host;
}

/** Everything the previews draw of the business: its name, logo, host, address, sender and newest room. */
export function usePreviewWho(data: SettingsResponse, room: PreviewRoom | null): PreviewWho {
  const { user } = useAuth();
  const business = user?.workspace_name || "Your business";
  const host = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || business;
  return {
    business,
    logoUrl: data.brand.logo_url,
    hostName: host,
    hostPhoto: user?.avatar_url ?? null,
    address: shownHost(data.domain),
    fromName: data.brand.from_name || business,
    fromEmail: data.sending.domain ? data.sending.from_email : null,
    footer: data.brand.footer_text,
    room,
  };
}

/** The business's name and logo, which live in Flashsuite, and the way there. */
export function FromFlashsuite({ business, logoUrl }: { business: string; logoUrl: string | null }) {
  const { flashsuite } = useAuth();
  return (
    <div className="fromfs">
      {logoUrl ? <img className="lg" src={logoUrl} alt="" style={{ objectFit: "cover" }} /> : <div className="lg">{initialsOf(business)}</div>}
      <div className="sp">
        <div className="b7">{business}</div>
      </div>
      {flashsuite ? (
        <a className="link" href={`${flashsuite.url}/business`} target="_blank" rel="noopener noreferrer">
          Change in Flashsuite <Icon name="ext" />
        </a>
      ) : null}
    </div>
  );
}

export type LookMode = LookId | "custom";

/** The six colour sets and Custom; Custom opens three colour pickers. */
export function LookPickers({ mode, colours, onPick }: { mode: LookMode; colours: Colours; onPick: (mode: LookMode, colours: Colours) => void }) {
  return (
    <div className="field">
      <div className="lbl">Colours</div>
      <div className="themes">
        {LOOKS.map((l) => (
          <button
            key={l.id}
            type="button"
            className={cx("th", mode === l.id && "on")}
            aria-pressed={mode === l.id}
            onClick={() => onPick(l.id, { accent: l.accent, brass: l.brass, paper: l.paper })}
          >
            <div className="sw">
              <i style={{ background: l.accent }} />
              <i style={{ background: l.brass }} />
              <i style={{ background: l.paper }} />
            </div>
            <div className="nm">
              {l.name}
              {mode === l.id ? <Icon name="check" /> : null}
            </div>
          </button>
        ))}
        <button type="button" className={cx("th cus", mode === "custom" && "on")} aria-pressed={mode === "custom"} onClick={() => onPick("custom", colours)}>
          <div className="sw" />
          <div className="nm">
            Custom
            {mode === "custom" ? <Icon name="check" /> : null}
          </div>
        </button>
      </div>
      {mode === "custom" ? (
        <div className="custom3">
          {(
            [
              ["accent", "Accent"],
              ["brass", "Secondary"],
              ["paper", "Page tint"],
            ] as const
          ).map(([k, label]) => (
            <label key={k} className="cpick">
              <input type="color" value={colours[k]} aria-label={label} onChange={(e) => onPick("custom", { ...colours, [k]: e.target.value })} />
              <div>
                <div className="lb">{label}</div>
                <code className="hx">{colours[k]}</code>
              </div>
            </label>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export type RecState = "found" | "checking" | "wait";

/** How far a record or a connection has got: found, being checked, or waiting. */
export function RecStatus({ state, found = "Found" }: { state: RecState; found?: string }) {
  if (state === "found") {
    return (
      <span className="rst found">
        <span className="ck">
          <Icon name="check" />
        </span>
        {found}
      </span>
    );
  }
  if (state === "checking") {
    return (
      <span className="rst checking">
        <span className="spin" />
        Checking
      </span>
    );
  }
  return (
    <span className="rst">
      <span className="o" />
      Waiting
    </span>
  );
}

/** One DNS record to add: its type, whether it is there yet, and its name and value to copy. */
export function RecCard({ rec, root, state }: { rec: { type: string; name: string; value: string }; root: string; state: RecState }) {
  const name = recordName(rec.name, root);
  return (
    <div className={cx("rec", state === "found" && "found")}>
      <div className="rh">
        <code className="ty">{rec.type}</code>
        {rec.type.toUpperCase() === "MX" ? <span className="xs muted nowrap">Priority {MX_PRIORITY}</span> : null}
      </div>
      <RecStatus state={state} />
      <div className="vals">
        <CopyValue label="Name" value={name} title="Copy the name" />
        <CopyValue label="Value" value={rec.value} title="Copy the value" />
      </div>
    </div>
  );
}

/**
 * A field joined to the words after it (`.ig.join`): the input grows with what is typed, so the fixed part reads
 * straight on from it, and a press anywhere on the field goes to the input.
 */
export function JoinInput({
  id,
  value,
  onChange,
  onBlur,
  suffix,
  large = false,
  label,
  disabled = false,
}: {
  id?: string;
  value: string;
  onChange: (v: string) => void;
  onBlur?: () => void;
  suffix: ReactNode;
  large?: boolean;
  label?: string;
  disabled?: boolean;
}) {
  const input = useRef<HTMLInputElement>(null);
  useLayoutEffect(() => {
    const el = input.current;
    const ctx = el ? document.createElement("canvas").getContext?.("2d") : null;
    if (!el || !ctx) return;
    const fit = () => {
      const cs = getComputedStyle(el);
      ctx.font = `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      const w = ctx.measureText(value || el.placeholder || " ").width;
      el.style.width = `${Math.ceil(w + (Number.parseFloat(cs.paddingLeft) || 0) + 2)}px`;
    };
    fit();
    // Why again once the fonts are in: measured in the fallback face, the last letter can fall under the words after it.
    void document.fonts?.ready.then(fit);
  }, [value]);
  return (
    // Why a click on the box: the input inside is the control, and a press on the fixed words should land in it.
    <div className={cx("ig join", large && "lg")} onClick={() => input.current?.focus()}>
      <input dir="auto"
        ref={input}
        id={id}
        className={cx("inp fit", large && "lg")}
        value={value}
        aria-label={label}
        spellCheck={false}
        autoCapitalize="none"
        autoComplete="off"
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
      />
      <span className="fix">{suffix}</span>
    </div>
  );
}
